// ============================================
// Path: /frontend/src/hooks/useToast.js
// Purpose: Toast notification system with context provider
// ============================================

import { useState, createContext, useContext } from 'react';

// ============================================
// TOAST CONTEXT - Provides toast functionality app-wide
// ============================================
const ToastContext = createContext(null);

// ============================================
// TOASTER COMPONENT - Renders toast notifications
// ============================================
export function Toaster({ children }) {
  const [toasts, setToasts] = useState([]);

  // Add new toast notification
  const addToast = ({ title, description, variant = 'default' }) => {
    const id = Math.random().toString(36).substring(7);
    const newToast = { id, title, description, variant };
    
    console.log('[TOASTER] Adding toast:', newToast);
    setToasts((prev) => [...prev, newToast]);

    // Auto remove after 7 seconds
    setTimeout(() => {
      console.log('[TOASTER] Removing toast:', id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 7000);
  };

  // Manually remove toast
  const removeToast = (id) => {
    console.log('[TOASTER] Manually removing toast:', id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      
      {/* Toast Container - Fixed position top-right */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-md pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`p-4 rounded-lg shadow-lg border pointer-events-auto transition-all duration-300 ${
              toast.variant === 'destructive'
                ? 'bg-red-50 border-red-200 text-red-900'
                : 'bg-white border-gray-200 text-gray-900'
            }`}
            style={{
              animation: 'slideInFromRight 0.3s ease-out'
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                {toast.title && (
                  <div className="font-semibold text-sm mb-1">
                    {toast.title}
                  </div>
                )}
                {toast.description && (
                  <div className="text-sm opacity-90">
                    {toast.description}
                  </div>
                )}
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none transition-colors"
                aria-label="Close notification"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ============================================
// USE TOAST HOOK - Access toast functionality
// ============================================
export function useToast() {
  const context = useContext(ToastContext);
  
  if (!context) {
    // Fallback if used outside provider
    console.warn('[useToast] Used outside ToastContext provider - using fallback');
    return {
      toast: ({ title, description, variant }) => {
        console.log('[TOAST - No Provider]:', { title, description, variant });
      }
    };
  }

  return {
    toast: context.addToast
  };
}